/**
 * Specialized hook for Organization operations
 *
 * @module @shared/services/hooks/useOrgApi
 */

import { useCallback } from 'react';
import { useApiCore } from './useApiCore';
import type { Org } from '@shared/models/requestTypes';
import type { ApiResult } from '../apiService';

/**
 * Hook that provides Organization-specific API methods with automatic authentication.
 *
 * @returns Object with Organization methods
 *
 * @example
 * ```typescript
 * const { get } = useOrgApi();
 *
 * const org = await get();
 * ```
 */
export function useOrgApi(): {
    get: () => ApiResult<Org>;
} {
    const { getToken, apiService } = useApiCore();

    return {
        get: useCallback(async (): ApiResult<Org> => {
            const token = await getToken();
            return apiService.org.get(token);
        }, [getToken, apiService]),
    };
}
